"use client";
import { useEffect, useState } from "react";
import { CircleAlert } from "lucide-react";
import { isMaintenanceActive } from "@/components/maintenance/flag";

// First-party ad-block dialog, visually + behaviourally matched to the Angular
// sticko-web `AdBlockDialogComponent` (red "Ads" badge, "Please disable your ad
// blocker!" copy, full-width red "I've disabled my ad blocker!" button that
// reloads). Like the Angular dialog it opens with `disableClose: true` — there
// is no dismiss; the only action reloads `/`, which re-runs detection so the
// dialog reappears until the blocker is actually off.
//
// The Angular app detects via the `adblock-detector` npm package; that package
// isn't in this project, so we use the equivalent first-party "bait" probe: a
// hidden element with ad-network class names that blockers hide/collapse.
export function AdBlockDialog() {
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    // Defer detection until the browser is idle so the modal can never
    // appear during the LCP window — modal pop-in mid-paint is one of the
    // largest CLS sources on this page. requestIdleCallback waits for the
    // main thread to settle; setTimeout is the fallback for Safari (which
    // still doesn't ship rIC as of 2026).
    // Never run during maintenance. This dialog is un-dismissable and its only
    // button reloads `/`, so it would trap the visitor in a loop telling them
    // to disable an ad blocker — and no ads are being requested anyway, so the
    // bait probe would false-positive on everyone.
    if (isMaintenanceActive()) return;
    let bait: HTMLDivElement | null = null;
    let probeTimer: ReturnType<typeof setTimeout> | null = null;
    const detect = () => {
      bait = document.createElement("div");
      bait.className = "adsbox ad-banner ad-placement google-ads adsbygoogle";
      bait.style.cssText = "position:absolute;left:-9999px;top:-9999px;height:10px;width:10px;";
      document.body.appendChild(bait);
      probeTimer = setTimeout(() => {
        if (!bait) return;
        const isBlocked =
          bait.offsetParent === null ||
          bait.clientHeight === 0 ||
          getComputedStyle(bait).display === "none";
        bait.remove();
        bait = null;
        if (isBlocked) setBlocked(true);
      }, 200);
    };
    const hasRic = typeof window.requestIdleCallback === "function";
    const handle = hasRic
      ? window.requestIdleCallback(detect, { timeout: 4000 })
      : window.setTimeout(detect, 3000);
    return () => {
      if (hasRic) {
        window.cancelIdleCallback?.(handle as number);
      } else {
        window.clearTimeout(handle as number);
      }
      if (probeTimer) clearTimeout(probeTimer);
      bait?.remove();
    };
  }, []);

  if (!blocked) return null;

  // Mirrors the Angular `reload()` — full navigation to `/` so detection runs
  // again from a clean load.
  const reload = () => {
    window.location.assign("/");
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ab-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-100 rounded-[10px] bg-white px-12 py-8">
        {/* "Ads" badge: red dotted circle with an exclamation icon at the
            top-right corner — matches the Angular .image-container layout. */}
        <div className="flex justify-center">
          <div className="relative w-25 text-center">
            <div className="flex aspect-square items-center justify-center rounded-full border-4 border-dotted border-[#fa3123] text-3xl font-bold text-[#fa3123]">
              Ads
            </div>
            <CircleAlert
              aria-hidden
              className="absolute -top-1 -right-1 h-8 w-8 rounded-full bg-white text-[#fa3123]"
            />
          </div>
        </div>

        <div className="mt-4 text-center">
          <h3 id="ab-title" className="text-foreground text-3xl font-bold">
            Please disable your ad blocker!
          </h3>
          <p className="text-muted-foreground my-5 font-medium">
            We know ads are annoying but please bear with us here &amp; disable your ad blocker!
          </p>
        </div>

        <button
          onClick={reload}
          className="w-full rounded-[30px] border-2 border-[#fa3123] bg-[#fa3123] px-5 py-4 font-medium text-white transition-colors hover:bg-white hover:text-[#fa3123]"
        >
          I&apos;ve disabled my ad blocker!
        </button>
      </div>
    </div>
  );
}
